import {
  CatchBoundary,
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { asUserId } from '@leanin/shared/ids';
import {
  gateOnboardingComplete,
  memberPhotoUrl,
  sessionQuery,
  useSession,
} from '@/lib/use-session';
import { takeAuthDrift } from '@/lib/queries/identity';
import { ReactivateAccount } from '@/components/auth/ReactivateAccount';
import { myPinsQuery, useMyPins } from '@/lib/queries/pins';
import { Shell } from '@/components/shell/Shell';
import { SessionErrorPage } from '@/components/shell/SessionErrorPage';
import { PageErrorState } from '@/components/shell/PageErrorState';
import { ViewerProvider, type ViewerIdentity } from '@/lib/viewer-context';
import { viewerWithAvatarSrc } from '@/lib/viewer';

// Auth gate + onboarding gate + app shell. Runs before any nested authed
// route renders. Pre-warms session + pins via TanStack Query so child
// components reading `useSession()` / `useMyPins()` get a cache hit on
// first render. Unauthenticated visitors are bounced to `/auth/login`;
// authenticated-but-not-onboarded visitors are bounced to
// `/onboarding/profile`.
//
// Why the onboarding gate lives HERE: `onboardingCompletedAt === null`
// is the single signal that a user hasn't finished the persona tour
// (the leader-grant + member-provision both completed during signup,
// but the recipient never got the leader-tour). Without this gate, a
// new invitee who closes the tab after `/auth/finishing` and re-opens
// directly at `/feed` or `/networks/{id}` lands there with the role
// but never sees their tour — the only previous code path that caught
// this was LoginForm's mid-onboarding re-route, which fires only when
// they manually visit `/auth/login`.
//
// The onboarding routes live OUTSIDE `_authed` (at `/onboarding/*`), so
// this gate won't create a loop with them — they handle their own auth
// check. Auth pages (`/auth/*`) likewise aren't under `_authed`.
//
// Error classification: the session probe resolving NULL is the
// DEFINITIVE signed-out verdict (the server answered a bare 401/404) →
// redirect to /auth/login. The probe THROWING (network failure, 5xx,
// Clerk outage — react-query has already exhausted its retries) means
// the auth state is UNKNOWN → let the error propagate so the route's
// `errorComponent` renders a retryable surface. Classifying "couldn't
// determine" as "signed out" bounced genuinely signed-in users to the
// login page whenever the first probe failed.
export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionQuery);
    if (ctx === null) {
      // A drift-final verdict (restricted / past-window deactivation) rides
      // along so the login page can explain the forced sign-out instead of
      // rendering a wordless form.
      const reason = takeAuthDrift();
      throw redirect({
        to: '/auth/login',
        search: { from: location.href, ...(reason !== null ? { reason } : {}) },
      });
    }
    // Onboarding gate — shared with `_public` so the same rule covers
    // signed-in viewers on auth-optional routes (e.g. `/networks/{id}`
    // is under `_public` because anonymous visitors can browse). See
    // `gateOnboardingComplete` for the rule definition.
    gateOnboardingComplete(ctx);
    // Pin prefetch — non-blocking; shell handles loading state.
    context.queryClient.prefetchQuery(myPinsQuery);
    return { session: ctx.session, member: ctx.member };
  },
  errorComponent: SessionErrorPage,
  component: AuthedShell,
});

function AuthedShell() {
  const { data: ctx } = useSession();
  const { data: pins } = useMyPins();
  // Reset the page-error boundary on navigation: a crashed page recovers as
  // soon as the pathname changes (and "Try again" re-renders the same one).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (!ctx) return null;
  const { member } = ctx;
  // Deactivated-within-window: the session is live (see the server's
  // 30-day grace in `loadSessionFromClerk`) but every write 403s. Show the
  // reactivation prompt instead of a shell that silently rejects everything —
  // this is the "sign back in within 30 days to restore" flow the settings
  // deactivation copy promises.
  if (member.deactivatedAt !== null) {
    return <ReactivateAccount deactivatedAt={member.deactivatedAt} />;
  }
  const photoUrl = memberPhotoUrl(member) ?? null;
  const viewer: ViewerIdentity = {
    id: asUserId(member.id),
    displayName: `${member.firstName} ${member.lastName}`.trim(),
    photoUrl,
  };
  const shellUser = {
    ...viewerWithAvatarSrc({
      id: member.id,
      firstName: member.firstName,
      lastName: member.lastName,
      photoUrl: photoUrl ?? undefined,
    }),
    platformRole: member.platformRole,
  };
  // Two distinct error surfaces:
  //   - `_authed`'s route `errorComponent` (SessionErrorPage) catches
  //     beforeLoad/session-probe failures → "couldn't load your session".
  //   - this inner CatchBoundary catches a RENDER error thrown by a page,
  //     BELOW the session boundary, so a page bug shows a genuine page error
  //     (shell intact) instead of masquerading as a session/network failure.
  return (
    <ViewerProvider value={viewer}>
      <Shell user={shellUser} pins={pins ?? []}>
        <CatchBoundary getResetKey={() => pathname} errorComponent={PageErrorState}>
          <Outlet />
        </CatchBoundary>
      </Shell>
    </ViewerProvider>
  );
}
